import React, { useEffect } from "react";
import axios from "axios";
import { useInfiniteQuery } from "@tanstack/react-query";
import baseURL from "../utils/BaseURL"; // Adjust path as needed

const fetchCategories = async ({ pageParam = 1 }) => {
  const { data } = await axios.get(`${baseURL}/categories?page=${pageParam}`);
  if (!data.success) throw new Error("Failed to fetch categories");
  return data;
};

const CategorySidebar = ({
  selectedCategoryId,
  onSelectCategory,
  setDefaultCategory,
}) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.result.pagination;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    staleTime: 1000 * 60 * 10, // Cache categories for 10 minutes
  });

  useEffect(() => {
    if (data?.pages[0]?.result?.categories?.length > 0) {
      const firstCategory = data.pages[0].result.categories[0];
      setDefaultCategory(firstCategory.id, firstCategory.name);
    }
  }, [data, setDefaultCategory]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const threshold = 120; // pixels from bottom

    if (
      scrollHeight - scrollTop - clientHeight <= threshold &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-6"></div>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-4 bg-gray-100 rounded w-full mb-4"></div>
        ))}
      </div>
    );
  }

  if (isError)
    return <div className="text-red-500">Failed to load categories.</div>;

  const categories = data.pages.flatMap((page) => page.result.categories);

  const handleMobileScroll = (event) => {
    const { scrollLeft, scrollWidth, clientWidth } = event.target;
    const threshold = 120;

    if (
      scrollWidth - scrollLeft - clientWidth <= threshold &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <>
      {/* Mobile sticky top horizontal pill bar */}
      <div className="block md:hidden bg-white rounded-2xl p-3 shadow-sm border border-gray-100 sticky top-16 z-10">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#15363A]">Categories</h3>
          {isFetchingNextPage && (
            <div className="text-xs text-[#15363A]">Loading...</div>
          )}
        </div>
        <div
          onScroll={handleMobileScroll}
          className="flex gap-2 overflow-x-auto pb-2 "
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {categories.map((category) => {
            const isActive = selectedCategoryId === category.id;
            return (
              <button
                key={category.id}
                onClick={() => onSelectCategory(category.id, category.name)}
                className={`shrink-0 px-3 py-1 rounded-full  transition-colors duration-200 flex items-center gap-2 ${
                  isActive
                    ? "bg-[#998147] text-[#f8f6f1]  font-semibold"
                    : " bg-white text-gray-600 hover:border-[#998147] hover:text-[#15363A]"
                }`}
              >
                {/* <img
                  src={category.image}
                  alt={category.name}
                  className="w-6 h-6 rounded-full object-cover"
                /> */}
                <span className="whitespace-nowrap">{category.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop sidebar list */}
      <div className="hidden md:block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
        <h3 className="text-xl font-bold text-[#15363A] mb-6 border-b border-gray-100 pb-4">
          Product Categories
        </h3>

        <ul
          onScroll={handleScroll}
          className="flex flex-col gap-2 h-[27rem] overflow-y-scroll"
        >
          {data.pages.map((page, pageIndex) => (
            <React.Fragment key={pageIndex}>
              {page.result.categories.map((category) => {
                const isActive = selectedCategoryId === category.id;

                return (
                  <li key={category.id}>
                    <button
                      onClick={() =>
                        onSelectCategory(category.id, category.name)
                      }
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 group ${
                        isActive
                          ? "bg-[#f8f6f1] text-[#998147] font-bold"
                          : "text-gray-500 hover:bg-gray-50 hover:text-[#15363A] font-medium"
                      }`}
                    >
                      <img
                        src={category.image}
                        className="size-10"
                        alt={category.name}
                      />
                      {category.name}
                    </button>
                  </li>
                );
              })}
            </React.Fragment>
          ))}
        </ul>
        {isFetchingNextPage && (
          <div className="mt-4 w-full flex justify-center items-center gap-2 text-sm text-[#15363A]">
            <div className="w-4 h-4 border-2 border-[#998147] border-t-transparent rounded-full animate-spin"></div>
            Loading more categories...
          </div>
        )}
      </div>
    </>
  );
};

export default CategorySidebar;
